/**
 * Date formatter that preserves exact calendar dates for practice closures
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string preserving the exact calendar date
 */
export const formatDateInPracticeTimezone = (
  dateString: string,
  _timezone?: string | null,
  _state?: string | null // eslint-disable-line @typescript-eslint/no-unused-vars
): string => {
  try {
    // Parse date string manually to avoid timezone conversion issues
    const [year, month, day] = dateString.split('-').map(Number);

    // If no timezone provided, use local timezone (fallback to current fix)
    if (!_timezone) {
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }

    // For practice closures, we want to display the exact calendar date
    // without timezone conversion that could shift the date
    const date = new Date(year, month - 1, day);

    // Format date without timezone conversion to preserve the exact calendar date
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date with timezone:', error);

    // Fallback to simple date parsing (current fix)
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
};

/**
 * Get practice timezone for a given practice ID
 * This is a helper function to fetch practice details when needed
 */
export const getPracticeTimezone = async (
  practiceId: string
): Promise<string | null> => {
  try {
    const { getPracticeById } = await import(
      '../services/practices/getPracticeById'
    );
    const { data } = await getPracticeById(practiceId);
    return data?.practice?.timezone || null;
  } catch (error) {
    console.error('Error fetching practice timezone:', error);
    return null;
  }
};
